/**
 * advisorIndex — the PURE half of the office Lookup transport (plan 10-06).
 *
 * Everything here is data → data: the spine contracts, the register-row →
 * index-entry mapping (visibility + open-target resolution), the Sources
 * parser and the citation → register matcher. No MSAL, no Graph, no DOM —
 * so it is testable in the `node` vitest environment. The transport
 * (`advisorClient.ts`) owns tokens, Graph reads, the 10-minute cache and
 * `openDoc`, and imports from here.
 *
 * The matching rules are a TypeScript port of
 * harmony-qms-fieldlink/api/chat/index.js + api/shared/docresolve.js
 * (05-06/05-08, re-aligned 10-04/10-06) — keep them aligned with that source
 * when the fieldlink side evolves. Divergences that are DELIBERATE on the
 * office surface are named inline (no tier gate; direct `webUrl` open; no
 * alias contract).
 */

import type { Citation, LibraryMatch } from '../components/AskOrSearch/types';

// ---------------------------------------------------------------------------
// Spine contracts — ISS-131 / D-10-03-1 (a)
//
// Visibility on BOTH surfaces keys on the OWNED lifecycle: `ApprovalStatus`,
// written by the Phase 9 approval walk (METADATA-SCHEMA §3.2). This mirrors
// harmony-qms-fieldlink `api/shared/docresolve.js` SPINES at `4e8ec92`
// (2026-09-19) so the office and field surfaces agree by construction.
// Centro's `PolicyStatus` / `ProcessStatus` are trap column T3 — never read,
// never written, not even fetched. Allowlist (not a denylist) so a blank or
// unknown status fails closed; the Active status is deliberately absent
// (D-10-03-3) — adding it is one word if that decision is overturned.
// ---------------------------------------------------------------------------

export const MAX_CITATIONS = 10;

/** The one status column visibility may key on — the owned lifecycle. */
export type OwnedStatusField = 'ApprovalStatus';

export interface Spine {
  kind: 'policy' | 'process';
  listTitle: string;
  idField: 'PolicyID' | 'ProcessID';
  statusField: OwnedStatusField;
  visibleStatuses: string[];
  folders: { ndis: string; agedCare: string };
}

export const SPINES: { policy: Spine; process: Spine } = {
  policy: {
    kind: 'policy',
    listTitle: 'Policy Register',
    idField: 'PolicyID',
    statusField: 'ApprovalStatus',
    visibleStatuses: ['Approved'],
    folders: { ndis: 'Policies_DS', agedCare: 'Policies_HC' },
  },
  process: {
    kind: 'process',
    listTitle: 'Process Register',
    idField: 'ProcessID',
    statusField: 'ApprovalStatus',
    visibleStatuses: ['Approved'],
    folders: { ndis: 'Processes_DS', agedCare: 'Processes_HC' },
  },
};

/**
 * The Graph `$select` on both registers. The owned governance columns
 * (`ApprovalStatus`, `Reference`, `Version`, `EffectiveDate`) exist on both
 * spines (09-11 / `extend_registers.py`); a missing value arrives as
 * `undefined`, never a throw. `CentroUrl` stays for the open-target fallback.
 */
export const REGISTER_SELECT: readonly string[] = [
  'PolicyID',
  'ProcessID',
  'Title',
  'Category',
  'Sector',
  'ApprovalStatus',
  'Reference',
  'Version',
  'EffectiveDate',
  'CentroUrl',
];

export interface RegisterRow {
  id: string;
  PolicyID?: string;
  ProcessID?: string;
  Title?: string;
  Category?: string;
  Sector?: string;
  ApprovalStatus?: string;
  /** The owned reference — `SaH-POL-090` / `NDIS-PRC-043` / `ORG-POL-134`. */
  Reference?: string;
  /** The owned version, a string (`'1.0'`) — ISS-124. */
  Version?: string;
  EffectiveDate?: string;
  CentroUrl?: string;
}

// ---------------------------------------------------------------------------
// Title/filename normalization (port of docresolve.js)
// ---------------------------------------------------------------------------

export function normalizeKey(value: unknown): string {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function splitExtension(name: string): { stem: string; ext: string } {
  const m = /^(.*)\.([A-Za-z0-9]{1,8})$/.exec(String(name || ''));
  if (!m) return { stem: String(name || ''), ext: '' };
  return { stem: m[1], ext: m[2].toLowerCase() };
}

/** Strip a leading register-id token ('pol 012 ', 'prc12 ') from a key. */
export function stripIdToken(key: string): string {
  return key.replace(/^(pol|prc)\s*\d+\s*/, '').trim();
}

export function sectorFolders(spine: Spine, sector: unknown): string[] {
  const s = typeof sector === 'string' ? sector.trim().toLowerCase() : '';
  if (s === 'ndis') return [spine.folders.ndis];
  if (s === 'aged care') return [spine.folders.agedCare];
  return [spine.folders.ndis, spine.folders.agedCare];
}

// ---------------------------------------------------------------------------
// The office lookup index — register rows + corpus files + open targets
// ---------------------------------------------------------------------------

export interface CorpusFile {
  name: string;
  folder: string;
  webUrl: string;
  key: string;
}

export interface IndexEntry {
  id: string; // POL-### / PRC-###
  kind: 'policy' | 'process';
  title: string;
  category: string | null;
  /** Office docState: 'available' ONLY when our own library file resolved (ISS-229). */
  docState: 'available' | 'unavailable';
  /** Direct open target — our library file's webUrl, or null. Never Centro. */
  openUrl: string | null;
  /**
   * The register's CentroUrl, carried ONLY when our own file did not resolve —
   * shown as the labelled secondary "Old copy in Centro" link, never opened
   * silently. A row resolved from our library never carries it.
   */
  centroUrl?: string;
  key: string;
  strippedKey: string;
  /** Owned reference (`SaH-POL-090`), when the register carries it. */
  reference?: string;
  /** Owned version (`'1.0'`) — carried onto the chip label (D-10-06-1). */
  version?: string;
  /** Fetched, not shown: a button chip cannot carry a tooltip (D-10-06-1). */
  effectiveDate?: string;
}

/** A corpus-folder read that failed (ISS-229) — for diagnostics and the page notice. */
export interface LibraryFailure {
  folder: string;
  /** The HTTP status, or null when the error carried none (a network fault). */
  status: number | null;
}

export interface LibraryStatus {
  reachable: boolean;
  failures: LibraryFailure[];
}

export interface LookupIndex {
  entries: IndexEntry[];
  byId: Map<string, IndexEntry>;
  /** False when any read of our policy library failed (ISS-229 b). */
  libraryReachable: boolean;
  /** The failed folder reads; empty when reachable. */
  libraryFailures: LibraryFailure[];
}

// The words the office surface shows when our own file can't be opened (13.1-10).
export const LIBRARY_UNREACHABLE_NOTICE =
  "Couldn't reach the QMS policy library for your account. Tell Andy.";
export const CENTRO_FALLBACK_LABEL = 'Old copy in Centro (may be out of date after 1 Dec)';
export const UNRESOLVED_HINT = {
  unreachable: "Our copy couldn't be reached from your account",
  unpublished: "Our copy isn't in the QMS library yet. Tell Andy",
} as const;

/** Resolve one register row to its office open target. */
export function resolveOfficeDoc(
  files: CorpusFile[],
  spine: Spine,
  row: RegisterRow,
): {
  docState: 'available' | 'unavailable';
  openUrl: string | null;
  source: 'library' | 'centro' | 'none';
  centroUrl?: string;
} {
  const titleKey = normalizeKey(row.Title);
  const folders = sectorFolders(spine, row.Sector);

  // After 10-03 every Approved row has `<Title>.docx` in its sector folder
  // (D-10-03-4: `Both` published once into `_HC`), so a title match opens the
  // owned 1.0 file. Folder-scoped: six titles exist in BOTH `_DS` and `_HC`
  // as distinct register rows (e.g. `Incident Management Policy` = POL-035 /
  // POL-101), and the sector decides which one.
  if (titleKey) {
    const matches = files.filter(
      (f) =>
        folders.includes(f.folder) &&
        (f.key === titleKey || stripIdToken(f.key) === titleKey),
    );
    if (matches.length > 0) {
      matches.sort((a, b) => a.name.localeCompare(b.name));
      return { docState: 'available', openUrl: matches[0].webUrl, source: 'library' };
    }
  }

  // No silent Centro fallback (ISS-229 b, 13.1-10). Our own file didn't
  // resolve — the library is unreachable for this account, or the row is
  // unpublished — so the row is NOT openable as ours. Its CentroUrl travels
  // as a secondary, labelled "old copy" link only; after the 1 Dec write-lock
  // it may be out of date.
  const centroUrl = typeof row.CentroUrl === 'string' ? row.CentroUrl.trim() : '';
  if (centroUrl) return { docState: 'unavailable', openUrl: null, source: 'centro', centroUrl };

  return { docState: 'unavailable', openUrl: null, source: 'none' };
}

const warnedCentroFallback = new Set<string>();

/** Test hook: forget which ids have already been warned about. */
export function resetCentroFallbackWarnings(): void {
  warnedCentroFallback.clear();
}

function optionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Build the office index from raw spine reads: the visibility allowlist, then
 * the row → entry mapping with the open target resolved per row.
 */
export function buildIndex(
  rowsByKind: { policy: RegisterRow[]; process: RegisterRow[] },
  files: CorpusFile[],
  libraryFailures: LibraryFailure[] = [],
): LookupIndex {
  const libraryReachable = libraryFailures.length === 0;
  const entries: IndexEntry[] = [];
  for (const spine of [SPINES.policy, SPINES.process]) {
    for (const row of rowsByKind[spine.kind]) {
      const status = row[spine.statusField];
      if (!spine.visibleStatuses.includes(typeof status === 'string' ? status.trim() : '')) {
        continue;
      }
      const id = row[spine.idField];
      const title = row.Title;
      if (!id || typeof title !== 'string' || !title.trim()) continue;
      const upperId = String(id).toUpperCase();
      const resolved = resolveOfficeDoc(files, spine, row);
      // An unreachable library is ONE problem (the transport warns once), not
      // one warning per row.
      if (libraryReachable && resolved.source === 'centro' && !warnedCentroFallback.has(upperId)) {
        warnedCentroFallback.add(upperId);
        console.warn(
          `[advisorIndex] ${upperId} is Approved but has no published library file — offering the Centro copy as a secondary link`,
        );
      }
      entries.push({
        id: upperId,
        kind: spine.kind,
        title,
        category:
          typeof row.Category === 'string' && row.Category ? row.Category : null,
        docState: resolved.docState,
        openUrl: resolved.openUrl,
        key: normalizeKey(title),
        strippedKey: citationKey(title),
        reference: optionalString(row.Reference),
        version: optionalString(row.Version),
        effectiveDate: optionalString(row.EffectiveDate),
        ...(resolved.centroUrl ? { centroUrl: resolved.centroUrl } : {}),
      });
    }
  }
  const byId = new Map(entries.map((e) => [e.id, e]));
  return { entries, byId, libraryReachable, libraryFailures: [...libraryFailures] };
}

/**
 * The affordance for an entry our library did not resolve: why (a hint) and,
 * when the register has one, the labelled Centro copy. Empty for an available
 * entry — a library hit never shows a Centro link.
 */
export function unresolvedAffordance(
  entry: IndexEntry,
  index: Pick<LookupIndex, 'libraryReachable'>,
): { hint?: string; fallback?: { label: string; url: string } } {
  if (entry.docState === 'available') return {};
  const hint = index.libraryReachable ? UNRESOLVED_HINT.unpublished : UNRESOLVED_HINT.unreachable;
  return entry.centroUrl
    ? { hint, fallback: { label: CENTRO_FALLBACK_LABEL, url: entry.centroUrl } }
    : { hint };
}

// ---------------------------------------------------------------------------
// Citation parsing (port of fieldlink api/chat/index.js — DEFENSIVE; the
// Advisor's "### Sources" format is a prompt convention, not an API contract)
// ---------------------------------------------------------------------------

export const SOURCES_HEADING_RE = /^ {0,3}#{1,6}\s*(?:\*\*)?\s*sources\b.*$/im;
const NEXT_HEADING_RE = /^ {0,3}#{1,6}\s+\S/m;
const URL_RE = /https?:\/\/[^\s)\]>"']+/g;

/** The owned reference grammar: `SaH-POL-090` / `NDIS-PRC-043` / `ORG-POL-134`. */
const OWNED_REF_RE = /\b((?:SaH|NDIS|ORG)-(?:POL|PRC)-\d{3})\b/i;
const OWNED_REF_RE_ALL = /\b(?:SaH|NDIS|ORG)-(?:POL|PRC)-\d{3}\b/gi;
/** A bare register id: `POL-090` / `PRC-056` (the /api/doc key). */
const REGISTER_ID_RE = /\b((?:POL|PRC)-\d{3})\b/i;

export function splitSources(content: string): { body: string; sourcesBlock: string } {
  const text = String(content || '');
  const headingMatch = SOURCES_HEADING_RE.exec(text);
  if (!headingMatch) return { body: text, sourcesBlock: '' };
  const start = headingMatch.index;
  const afterHeading = start + headingMatch[0].length;
  const rest = text.slice(afterHeading);
  const next = NEXT_HEADING_RE.exec(rest);
  const end = next ? afterHeading + next.index : text.length;
  return {
    body: (text.slice(0, start) + text.slice(end)).trim(),
    sourcesBlock: text.slice(afterHeading, end),
  };
}

/** Candidate ALTERNATE doc titles from parenthetical annotations. */
export function parentheticalTitles(rawItem: string): string[] {
  const alts: string[] = [];
  const parenRe = /\(([^)]+)\)/g;
  let m: RegExpExecArray | null;
  while ((m = parenRe.exec(String(rawItem || ''))) !== null) {
    for (const fragment of m[1].split(/[;,]/)) {
      const cleaned = fragment
        .replace(/\bv\d+\b.*$/i, '')
        .replace(/\bpp?\.\s*[\d–—-]+.*$/i, '')
        .replace(/\bsteps?\s*[\d–—-].*$/i, '')
        .trim();
      const looksLikeTitle =
        /^(DS|HC|HR|AC)\s*-/i.test(cleaned) ||
        /\b(policy|policies|process|processes|procedure|procedures)\b/i.test(cleaned);
      if (cleaned && cleaned.split(/\s+/).length >= 2 && looksLikeTitle) {
        alts.push(cleaned);
      }
    }
  }
  return alts;
}

export interface ParsedCitation {
  title: string;
  alts: string[];
  ref: string | null;
  url: string | null;
  /** A bare register id in the item (`register POL-090`), upper-cased. */
  registerId: string | null;
  /** An owned reference in the item (`SaH-POL-090`), as written. */
  ownedRef: string | null;
}

/** Parse a Sources block → citations (bullet items OR markdown table rows). */
export function parseSourceItems(sourcesBlock: string): ParsedCitation[] {
  const items: string[] = [];
  let current: string | null = null;
  for (const line of String(sourcesBlock || '').split('\n')) {
    const tableRow = /^\s*\|(.+)\|\s*$/.exec(line);
    if (tableRow) {
      if (current) {
        items.push(current);
        current = null;
      }
      const cells = tableRow[1].split('|').map((c) => c.trim());
      if (cells.every((c) => /^[-:\s]*$/.test(c))) continue;
      if (/^documents?$/i.test(cells[0] || '') || /source\s*ref/i.test(cells[1] || '')) continue;
      items.push(cells.join(' | '));
      continue;
    }
    const bullet = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (bullet) {
      if (current) items.push(current);
      current = bullet[1];
    } else if (current && line.trim()) {
      current += ` ${line.trim()}`;
    }
  }
  if (current) items.push(current);

  const citations: ParsedCitation[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const urlMatch = /(https?:\/\/[^\s)\]>"']+)/.exec(item);
    // Both shapes are case-insensitive: the owned references are mixed case
    // (`SaH-POL-090`); the live Advisor also writes them BARE in its Sources
    // line ("— SaH-POL-090 · v1.0 · register POL-090", 10-04 deviation 3).
    const refMatch =
      /(?:source[\s_-]?ref|\bref\b)\W{0,4}`?([a-z0-9][a-z0-9-]*)`?/i.exec(item) ||
      /`([a-z0-9]+(?:-[a-z0-9]+)+)`/i.exec(item) ||
      OWNED_REF_RE.exec(item);

    // D-10-06-1: the register id and the owned reference are captured from the
    // FULL item text so they resolve before any title fuzz. The bare id is
    // looked for only outside an owned reference (`SaH-POL-090` contains
    // `POL-090`, but that is the reference's spelling, not a bare id).
    const ownedMatch = OWNED_REF_RE.exec(item);
    const registerMatch = REGISTER_ID_RE.exec(item.replace(OWNED_REF_RE_ALL, ' '));

    let title = '';
    const bold = /\*\*(.+?)\*\*/.exec(item);
    if (bold) {
      title = bold[1];
    } else {
      title = item.split(/\s+[—–]\s+|\s+-\s+|\(|,|\|/)[0] || '';
    }
    title = title.replace(/\*+/g, '').replace(/\[|\]/g, '').trim();
    if (!title) continue;

    const dedupeKey = normalizeKey(item);
    if (!dedupeKey || seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    citations.push({
      title,
      alts: parentheticalTitles(item),
      ref: refMatch ? refMatch[1].toLowerCase() : null,
      url: urlMatch ? urlMatch[1] : null,
      registerId: registerMatch ? registerMatch[1].toUpperCase() : null,
      ownedRef: ownedMatch ? ownedMatch[1] : null,
    });
    if (citations.length >= MAX_CITATIONS) break;
  }
  return citations;
}

/** Scrub raw corpus URLs from answer text (the citation chips carry access). */
export function scrubUrls(text: string): string {
  return String(text || '').replace(URL_RE, '[see Sources below]');
}

// ---------------------------------------------------------------------------
// Citation → register matching (port of fieldlink chat/index.js)
// ---------------------------------------------------------------------------

export const COMPENDIUM_REFS = new Set(['policies-ds', 'policies-hc', 'processes-ds', 'processes-hc']);
export const COMPENDIUM_KEYS = new Set([
  'disability support policies',
  'home care policies',
  'disability support processes',
  'home care processes',
  'policies hc',
  'policies ds',
  'processes hc',
  'processes ds',
  'polices ds', // the DS policies compendium file is literally misspelled
]);

export function citationKey(value: unknown): string {
  return normalizeKey(value)
    .replace(/^(ds|hc)\s+/, '')
    .replace(
      /\s+(policy and procedure|policies and procedures|policy|policies|process|processes|procedure|procedures)$/,
      '',
    )
    .trim();
}

export function citationKindHint(
  citation: Pick<ParsedCitation, 'ref' | 'title'>,
  sourceText: string | null,
): 'policy' | 'process' | null {
  const ref = citation.ref || '';
  // The owned reference grammar first (10-04): `SaH-POL-090` / `sah-prc-055`.
  if (/-pol-/i.test(ref)) return 'policy';
  if (/-prc-/i.test(ref)) return 'process';
  if (/^policies-|policy/.test(ref)) return 'policy';
  if (/^processes-|process/.test(ref)) return 'process';
  const text = String(sourceText || citation.title || '');
  if (/\bpolic(y|ies)\b/i.test(text)) return 'policy';
  if (/\bprocess(es)?\b|\bprocedures?\b/i.test(text)) return 'process';
  return null;
}

export function disambiguate(
  matches: IndexEntry[],
  kindHint: 'policy' | 'process' | null,
): IndexEntry | null {
  if (matches.length === 1) return matches[0];
  if (matches.length > 1 && kindHint) {
    const byKind = matches.filter((e) => e.kind === kindHint);
    if (byKind.length === 1) return byKind[0];
  }
  return null;
}

/**
 * Match one parsed citation to a register entry, or null.
 *
 * Resolution order (D-10-06-1):
 *   0)  the register id written in the Sources item (`register POL-090`) —
 *       unique by construction, matched exactly; an id that names a row this
 *       surface does not show (held, hidden) stays UNMATCHED rather than
 *       falling to a title fuzz that would open a DIFFERENT document;
 *   0b) the owned reference (`SaH-POL-090`) against the register's
 *       `Reference` column, case-insensitively; a reference with no visible
 *       row (a retired alias — no alias contract on this surface; the
 *       Advisor's own `Note:` line explains it) falls through to the title;
 *   1)  the cited title VERBATIM (normalized) against the entry key, with
 *       kind-hint tie-breaking; then the genre-stripped key against the
 *       STRIPPED entry key;
 *   2)  unique containment, length-guarded.
 *
 * 10-04's rule, ported verbatim: a policy title never opens its process twin
 * — the genre-stripped candidate is compared only against the stripped entry
 * key, never the unstripped one. ("X Policy" is the policy, "X" its process
 * twin: the owned suite carries such pairs as two visible rows. Until 10-04
 * the stripped candidate was compared against the unstripped register key,
 * so "X Policy" matched the "X" process outright and the chip opened the
 * wrong document — observed live on `SaH-POL-085` → `PRC-056`, 2026-09-21.)
 */
export function matchCitation(index: LookupIndex, citation: ParsedCitation): IndexEntry | null {
  if (!index.entries.length) return null;

  // 0) The register id — the /api/doc key, unique by construction.
  if (citation.registerId) {
    return index.byId.get(citation.registerId.toUpperCase()) ?? null;
  }

  // 0b) The owned reference against the register's `Reference` column.
  if (citation.ownedRef) {
    const wanted = citation.ownedRef.toUpperCase();
    const byRef = index.entries.filter(
      (e) => typeof e.reference === 'string' && e.reference.toUpperCase() === wanted,
    );
    if (byRef.length === 1) return byRef[0];
  }

  // Candidate keys, ordered: the specific title, then parenthetical alts,
  // then ref/url stems. Each candidate carries its own kind hint.
  const candidates: Array<{ full: string; key: string; hint: 'policy' | 'process' | null }> = [];
  const push = (rawText: string | null, hintSource: string | null) => {
    if (!rawText) return;
    const full = normalizeKey(rawText);
    if (COMPENDIUM_KEYS.has(full)) return;
    const key = citationKey(rawText);
    if (!key) return;
    candidates.push({ full, key, hint: citationKindHint(citation, hintSource || rawText) });
  };
  push(citation.title, `${citation.title} ${citation.ref || ''}`);
  for (const alt of citation.alts) push(alt, alt);
  if (citation.ref && !COMPENDIUM_REFS.has(citation.ref)) {
    push(citation.ref.replace(/-/g, ' '), citation.ref);
  }
  if (citation.url) {
    const stem = /\/([^/?#]+?)(?:\.[A-Za-z0-9]{1,8})?(?:[?#]|$)/.exec(citation.url);
    if (stem) push(decodeURIComponent(stem[1]).replace(/[-_]+/g, ' '), null);
  }

  // 1) Exact match — verbatim title against the unstripped key first, then
  //    the stripped candidate against the STRIPPED entry key (never the
  //    unstripped one — the 10-04 twin rule).
  for (const { full, key, hint } of candidates) {
    const exactFull = index.entries.filter((e) => e.key === full);
    const fullHit = disambiguate(exactFull, hint);
    if (fullHit) return fullHit;
    const exactStripped = index.entries.filter((e) => e.strippedKey === key);
    const stripped = disambiguate(exactStripped, hint);
    if (stripped) return stripped;
    if (exactFull.length > 1 || exactStripped.length > 1) return null; // truly ambiguous
  }

  // 2) Unique containment, length-guarded.
  for (const { key, hint } of candidates) {
    if (key.length < 10) continue;
    const contains = index.entries.filter((e) => {
      const ek = e.strippedKey || e.key;
      if (!ek || ek.length < 10) return false;
      return ek.includes(key) || key.includes(ek);
    });
    const hit = disambiguate(contains, hint);
    if (hit) return hit;
  }
  return null;
}

/**
 * Office citation rewrite: matched register docs get a docId when openable —
 * NO tier gate (office staff open everything this surface can resolve).
 *
 * D-10-06-1: an available document's chip carries the register title and its
 * owned version (`Clinical Care Policy · v1.0`), composed HERE — the shared
 * AskOrSearch component is a copy of FieldLink's and is not edited on this
 * surface. `effectiveDate` is fetched but not shown (a button chip cannot
 * carry a tooltip without editing the copy).
 */
export function rewriteCitations(parsed: ParsedCitation[], index: LookupIndex | null): Citation[] {
  return parsed.map((citation) => {
    const out: Citation = { title: citation.title, ref: citation.ref };
    if (!index) return out;
    const entry = matchCitation(index, citation);
    if (!entry) return out;
    if (COMPENDIUM_KEYS.has(normalizeKey(citation.title))) out.title = entry.title;
    out.docState = entry.docState;
    if (entry.docState === 'available') {
      out.docId = entry.id;
      if (entry.version) out.title = `${entry.title} · v${entry.version}`;
    } else {
      Object.assign(out, unresolvedAffordance(entry, index));
    }
    return out;
  });
}

// ---------------------------------------------------------------------------
// References anywhere in the answer body (13.1-10). The Sources block is a
// prompt convention: when the Advisor's own document fetch fails it writes
// the references into its prose and no Sources block at all (6 Oct, "SAH-POL-101
// … NDIS-POL-035"). The body is then scanned for owned references and bare
// register ids, and ONLY rules 0 / 0b resolve them — no title fuzz on prose.
// ---------------------------------------------------------------------------

/** An owned reference or a bare register id, in one left-to-right scan. */
const ANY_REF_RE_ALL = /\b(?:(?:SaH|NDIS|ORG)-)?(?:POL|PRC)-\d{3}\b/gi;

/** Owned references and bare register ids in the body, in text order, deduped. */
export function bodyReferences(body: string): ParsedCitation[] {
  const out: ParsedCitation[] = [];
  const seen = new Set<string>();
  for (const m of String(body || '').matchAll(ANY_REF_RE_ALL)) {
    const token = m[0];
    const upper = token.toUpperCase();
    if (seen.has(upper)) continue;
    seen.add(upper);
    const owned = /^(?:SaH|NDIS|ORG)-/i.test(token);
    // `title: ''` and `ref: null` leave matchCitation no title candidates, so
    // only step 0 (register id) and step 0b (the `Reference` column) apply.
    out.push({
      title: '',
      alts: [],
      ref: null,
      url: null,
      registerId: owned ? null : upper,
      ownedRef: owned ? token : null,
    });
  }
  return out;
}

/**
 * The chips for one answer: the Sources block's, as before — unless it is
 * missing or matched nothing, when the body's references are resolved too and
 * their hits lead (deduped by register id, capped at MAX_CITATIONS).
 */
export function answerCitations(
  body: string,
  sourcesBlock: string,
  index: LookupIndex | null,
): Citation[] {
  const parsed = parseSourceItems(sourcesBlock);
  const fromSources = rewriteCitations(parsed, index);
  if (!index) return fromSources;
  if (parsed.some((c) => matchCitation(index, c))) return fromSources;

  const fromBody: Citation[] = [];
  const seenIds = new Set<string>();
  for (const ref of bodyReferences(body)) {
    const entry = matchCitation(index, ref);
    if (!entry || seenIds.has(entry.id)) continue;
    seenIds.add(entry.id);
    fromBody.push(...rewriteCitations([{ ...ref, title: entry.title }], index));
  }
  return [...fromBody, ...fromSources].slice(0, MAX_CITATIONS);
}

// ---------------------------------------------------------------------------
// Keyword title search (13.1-10). The library search used to match the WHOLE
// query as one substring, so a sentence ("can you pull up the incident
// management policy for me to read through") matched no title. Now the query
// is tokenised, filler and doc-type words drop out, and the keywords rank
// titles: every keyword hit first, then any keyword. Register ids and owned
// references in the query match directly and lead.
// ---------------------------------------------------------------------------

const STOP_WORDS = new Set(
  (
    'a an the and or of for to in on at by with about from into as is are be am was were ' +
    'i me my we us our you your it its this that these those there here ' +
    'can could would will should may might shall do does did have has had ' +
    'please pls thanks thank hi hey just also some any all ' +
    'pull up show open find get give see view read look looking through bring display ' +
    'search fetch grab need want where what which who whats copy latest current ' +
    'let lets know tell how why when'
  ).split(/\s+/),
);

const POLICY_WORDS = new Set(['policy', 'policies']);
const PROCESS_WORDS = new Set(['process', 'processes', 'procedure', 'procedures', 'sop', 'sops']);
const DOC_WORDS = new Set(['document', 'documents', 'doc', 'docs', 'file', 'files']);

const QUERY_REF_RE_ALL = /\b(?:(SaH|NDIS|ORG)-)?(POL|PRC)[-\s]?(\d{3})\b/gi;

export interface QueryKeywords {
  keywords: string[];
  kindHint: 'policy' | 'process' | null;
  registerIds: string[];
  ownedRefs: string[];
}

/** Tokenise a search query: references out, filler out, doc-type words → kind hint. */
export function queryKeywords(q: string): QueryKeywords {
  const registerIds: string[] = [];
  const ownedRefs: string[] = [];
  const rest = String(q || '').replace(QUERY_REF_RE_ALL, (_m, scope: string | undefined, genre: string, num: string) => {
    const id = `${genre}-${num}`.toUpperCase();
    if (scope) ownedRefs.push(`${scope}-${id}`.toUpperCase());
    else registerIds.push(id);
    return ' ';
  });

  const tokens = normalizeKey(rest).split(' ').filter(Boolean);
  let policy = false;
  let process = false;
  const keywords: string[] = [];
  const docTypeWords: string[] = [];
  for (const t of tokens) {
    if (POLICY_WORDS.has(t)) {
      policy = true;
      docTypeWords.push(t);
    } else if (PROCESS_WORDS.has(t)) {
      process = true;
      docTypeWords.push(t);
    } else if (DOC_WORDS.has(t)) {
      docTypeWords.push(t);
    } else if (!STOP_WORDS.has(t) && !keywords.includes(t)) {
      keywords.push(t);
    }
  }
  // A query of ONLY doc-type words ("policies") still searches on them.
  if (keywords.length === 0 && registerIds.length === 0 && ownedRefs.length === 0) {
    for (const t of docTypeWords) if (!keywords.includes(t)) keywords.push(t);
  }
  const kindHint = policy === process ? null : policy ? 'policy' : 'process';
  return { keywords, kindHint, registerIds, ownedRefs };
}

/** A light stem: plural / -ing / -ed / trailing e, never below four letters. */
function stem(word: string): string {
  const m = /^(.*?)(ing|ed|es|s|e)$/.exec(word);
  return m && m[1].length >= 4 ? m[1] : word;
}

/** A keyword hits a word when the stems agree or the keyword is a typed prefix. */
function keywordHits(keywordStem: string, wordStems: string[]): boolean {
  return wordStems.some(
    (w) => w === keywordStem || (keywordStem.length >= 3 && w.startsWith(keywordStem)),
  );
}

function stems(text: string | null | undefined): string[] {
  return normalizeKey(text).split(' ').filter(Boolean).map(stem);
}

/** The LibraryMatch shape the AskOrSearch component renders. */
export function toLibraryMatch(
  entry: IndexEntry,
  index: Pick<LookupIndex, 'libraryReachable'>,
): LibraryMatch {
  return {
    id: entry.id,
    kind: entry.kind,
    title: entry.title,
    category: entry.category,
    docState: entry.docState,
    ...unresolvedAffordance(entry, index),
  };
}

/** Rank the index against a search query (see the section note). */
export function searchIndex(index: LookupIndex, q: string): LibraryMatch[] {
  const { keywords, kindHint, registerIds, ownedRefs } = queryKeywords(q);

  const direct: IndexEntry[] = [];
  const directIds = new Set<string>();
  const addDirect = (e: IndexEntry | undefined) => {
    if (e && !directIds.has(e.id)) {
      directIds.add(e.id);
      direct.push(e);
    }
  };
  for (const ref of ownedRefs) {
    for (const e of index.entries) {
      if (e.reference && e.reference.toUpperCase() === ref) addDirect(e);
    }
  }
  for (const id of registerIds) addDirect(index.byId.get(id));

  const keywordStems = keywords.map(stem);
  const scored: Array<{
    entry: IndexEntry;
    all: boolean;
    inTitle: number;
    weight: number;
    order: number;
    length: number;
  }> = [];
  if (keywordStems.length > 0) {
    const titleStemsOf = index.entries.map((e) => stems(e.title));
    // A rarer keyword weighs more ("incident" over "management"), so among
    // partial hits the distinctive word leads.
    const idf = keywordStems.map((k) => {
      const df = titleStemsOf.filter((ws) => keywordHits(k, ws)).length;
      return Math.log((index.entries.length + 1) / (df + 1)) + 1;
    });
    index.entries.forEach((entry, order) => {
      if (directIds.has(entry.id)) return;
      const titleStems = titleStemsOf[order];
      const categoryStems = stems(entry.category);
      let inTitle = 0;
      let total = 0;
      let weight = 0;
      keywordStems.forEach((k, i) => {
        if (keywordHits(k, titleStems)) {
          inTitle += 1;
          total += 1;
          weight += idf[i];
        } else if (keywordHits(k, categoryStems)) {
          total += 1;
          weight += idf[i] / 2;
        }
      });
      if (total === 0) return;
      scored.push({
        entry,
        all: total === keywordStems.length,
        inTitle,
        weight,
        order,
        length: titleStems.length,
      });
    });
  }
  const kindRank = (e: IndexEntry) => (kindHint && e.kind === kindHint ? 0 : 1);
  scored.sort(
    (a, b) =>
      Number(b.all) - Number(a.all) ||
      b.inTitle - a.inTitle ||
      b.weight - a.weight ||
      kindRank(a.entry) - kindRank(b.entry) ||
      a.length - b.length ||
      a.order - b.order,
  );
  return [...direct, ...scored.map((s) => s.entry)].map((e) => toLibraryMatch(e, index));
}
